import { Alert, Box, Button, Stack } from "@mui/material";
import PickupLocation from "./PickupLocation";
import Banner from "./Banner";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAlert } from "hooks/alerts/useAlert";
import { fetchLocationInformation } from "hooks/locations/useLocationInformation";
import usePickup from "hooks/pickups/usePickup";
import { waitForPickupSaves } from "hooks/pickups/useUpdatePickup";
import PickupReason from "./PickupReason";
import PickupMainContact from "./PickupMainContact";
import PickupOtherContacts from "./PickupOtherContacts";
import PickupDate from "./PickupDate";
import { useState, useEffect, useCallback, useRef } from "react";
import StepCard from "./StepCard";
import useAnalytics from "hooks/analytics/useAnalytics";
import PickupItemsDetails from "./PickupItemsDetails";
import PickupItemsDetailsAccordion from "./PickupItemsDetailsAccordion";
import theme from "../../../lib/theme";
import Breadcrumb from "./Breadcrumb";
import useFeatureFlag from "hooks/useFeatureFlag";
import SiteInformationForm from "./SiteInformationForm";
import PickupImages from "./PickupImages";
import { MissingRequirementsContext } from "./requirementError";
import { missingScheduleRequirements, scrollToFirstMissingRequirement } from "./scheduleRequirements";

function SchedulePickup() {
  const navigate = useNavigate();
  const { alertError } = useAlert();
  const { pickup, refetch: refetchPickup } = usePickup();
  const { createAnalyticsEvent } = useAnalytics();
  const [reminderVisible, setReminderVisible] = useState(true);
  const [eventTracked, setEventTracked] = useState(false);
  const [missingRequirements, setMissingRequirements] = useState<string[]>([]);
  const { enabled: newLogisticsPageEnabled, loading: newLogisticsPageLoading } =
    useFeatureFlag("new_logistics_page_enabled");

  const newPageEnabled = newLogisticsPageEnabled && !newLogisticsPageLoading;
  let [activeStep, setActiveStep] = useState(0);
  const savePendingSiteInformationRef = useRef<() => Promise<boolean>>(async () => true);
  const registerPendingSiteInformationSave = useCallback((saveIfExpanded: () => Promise<boolean>) => {
    savePendingSiteInformationRef.current = saveIfExpanded;
  }, []);
  const savePendingMainContactRef = useRef<() => Promise<boolean>>(async () => true);
  const registerPendingMainContactSave = useCallback((saveSelectedContact: () => Promise<boolean>) => {
    savePendingMainContactRef.current = saveSelectedContact;
  }, []);
  const savePendingPickupTypeRef = useRef<() => Promise<boolean>>(async () => true);
  const registerPendingPickupTypeSave = useCallback((savePickupType: () => Promise<boolean>) => {
    savePendingPickupTypeRef.current = savePickupType;
  }, []);

  const requireCompleteRequest = useCallback(async () => {
    await savePendingPickupTypeRef.current();
    const siteInformationSaved = await savePendingSiteInformationRef.current();
    const contactSaved = await savePendingMainContactRef.current();
    if (!contactSaved) return false;

    await waitForPickupSaves();

    try {
      const result = await refetchPickup();
      const freshPickup = result.data?.data;
      if (result.isError || !freshPickup) {
        alertError("There was an error, please try again.");
        return false;
      }

      const siteInformation = await fetchLocationInformation(freshPickup.location?.uuid);
      const missing = missingScheduleRequirements({
        pickup: freshPickup,
        siteInformation,
        siteInformationSaved,
      });

      setMissingRequirements(missing);
      scrollToFirstMissingRequirement(missing);
      return missing.length === 0;
    } catch {
      alertError("There was an error, please try again.");
      return false;
    }
  }, [alertError, refetchPickup]);

  // @ts-ignore
  window.setActiveStep = setActiveStep;

  let advanceActiveStep = useCallback(
    (step: number) => {
      if (step > activeStep) {
        setActiveStep(step);
      }
    },
    [activeStep]
  );

  useEffect(() => {
    if (eventTracked) return;
    setEventTracked(true);
    createAnalyticsEvent("start_schedule_pickup");
  }, [eventTracked]);

  useEffect(() => {
    if (newLogisticsPageEnabled) {
      return;
    }

    if (!pickup) {
      return advanceActiveStep(1);
    }

    if (pickup.pickupContacts.length > 0) {
      return advanceActiveStep(6);
    }

    if (pickup.pickupImages.length > 0) {
      return advanceActiveStep(5);
    }

    if (pickup.pickupEstimatedItems.length > 0) {
      return advanceActiveStep(4);
    }

    if (pickup.reason) {
      return advanceActiveStep(3);
    }

    if (pickup?.location) {
      return advanceActiveStep(2);
    }

    advanceActiveStep(1);
  }, [pickup, pickup?.location, advanceActiveStep, newLogisticsPageEnabled]);

  useEffect(() => {
    const validPickupStates = ["draft", "missing_info", "scheduled", "pending_confirmation"];
    if (pickup && !validPickupStates.includes(pickup.state)) navigate("/pickups");
  }, [navigate, pickup]);

  return (
    <MissingRequirementsContext.Provider value={missingRequirements}>
      <div>
        <Breadcrumb />

        <Stack mt={{ md: 4, xs: 2 }} spacing={{ md: 5, xs: 3 }}>
          <Banner />

          {reminderVisible && !newLogisticsPageLoading && !newLogisticsPageEnabled && (
            <Alert
              severity="info"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                ".MuiAlert-icon": { alignItems: "center", color: theme.palette.primary.contrastText },
              }}
              onClose={() => {
                setReminderVisible(false);
              }}
            >
              <strong>Reminder:</strong> Before scheduling a pickup to have our team come on-site, please follow your
              internal processes to remove all locks and deprovision all devices that are no longer in use and ready to
              be collected. Releasing devices from Apple Business Manager or other Device Enrollment Programs prior to a
              pickup with Revivn will ensure the quickest processing times and help you avoid your final report from
              being put on hold.
            </Alert>
          )}

          <StepCard step={1} activeStep={activeStep} disable={newPageEnabled ? false : undefined}>
            <PickupLocation
              step={1}
              goToNextStep={() => {
                if (!newPageEnabled) advanceActiveStep(2);
              }}
            />
          </StepCard>

          {newPageEnabled && (
            <StepCard step={2} activeStep={activeStep} disable={false}>
              <SiteInformationForm step={2} registerPendingSave={registerPendingSiteInformationSave} />
            </StepCard>
          )}

          <StepCard step={newPageEnabled ? 3 : 2} activeStep={activeStep} disable={newPageEnabled ? false : undefined}>
            <PickupReason
              step={newPageEnabled ? 3 : 2}
              goToNextStep={() => {
                if (!newPageEnabled) advanceActiveStep(3);
              }}
              registerPendingSave={registerPendingPickupTypeSave}
            />
          </StepCard>

          <StepCard step={newPageEnabled ? 4 : 3} activeStep={activeStep} disable={newPageEnabled ? false : undefined}>
            {newPageEnabled ? (
              <PickupItemsDetailsAccordion step={4} beforeSubmit={requireCompleteRequest} />
            ) : (
              <PickupItemsDetails
                step={4}
                goToNextStep={activeStep <= 4 || 4 ? () => advanceActiveStep(5) : undefined}
              />
            )}
          </StepCard>

          {newPageEnabled && (
            <StepCard step={5} activeStep={activeStep} disable={false}>
              <PickupImages step={5} />
            </StepCard>
          )}

          <StepCard step={newPageEnabled ? 6 : 5} activeStep={activeStep} disable={newPageEnabled ? false : undefined}>
            <PickupMainContact
              step={newPageEnabled ? 6 : 5}
              goToNextStep={() => {
                if (!newPageEnabled) advanceActiveStep(6);
              }}
              registerPendingSave={registerPendingMainContactSave}
            />
          </StepCard>

          <StepCard step={newPageEnabled ? 7 : 6} activeStep={activeStep} disable={newPageEnabled ? false : undefined}>
            <PickupOtherContacts step={newPageEnabled ? 7 : 6} />
          </StepCard>

          <StepCard
            step={newPageEnabled ? 8 : 7}
            activeStep={activeStep}
            disable={
              newPageEnabled
                ? pickup?.state === "pending_confirmation" || pickup?.state === "scheduled"
                : activeStep < 6 || pickup?.state === "pending_confirmation" || pickup?.state === "scheduled"
            }
          >
            <PickupDate step={newPageEnabled ? 8 : 7} />
          </StepCard>

          {pickup && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button component={RouterLink} size="large" variant="outlined" to={`/pickups`}>
                Back
              </Button>
              <Button form="pickup-estimated-items-form" type="submit" size="large" variant="contained">
                {newLogisticsPageEnabled ? "Submit Request" : pickup.state === "missing_info" ? "Next" : "Save"}
              </Button>
            </Box>
          )}
        </Stack>
      </div>
    </MissingRequirementsContext.Provider>
  );
}

export default function SchedulePickupWrapper() {
  return <SchedulePickup />;
}
